import { useState, useEffect, useCallback } from 'react'
import { Layers, Search, Settings, GripVertical, Package, Info, X, Loader2, Edit2 } from 'lucide-react'
import RRToggle from '@/components/rr/RRToggle'
import { rrGetCategories, rrGetCategoryProducts, rrToggleProductRR, rrUpdateCategory } from '@/actions/rr'

interface Category { id: number; uuid: string; name: string; colour: string; display_order: number; is_active: boolean; product_count: number; rr_count: number }
interface Product { cp_id: number; product_id: number; shopify_product_id: string; name: string; sku: string; price: string; product_status: string; rr_enabled: boolean }


interface EditCategoryModalProps { cat: Category; onClose: () => void; onSaved: () => void }
function EditCategoryModal({ cat, onClose, onSaved }: EditCategoryModalProps) {
  const SWATCHES = ['#157a4e', '#2f6bff', '#c4502f', '#8a5cd1', '#b9772e', '#2aa1a8', '#c2557a', '#3b82c4']
  const [name, setName] = useState(cat.name)
  const [colour, setColour] = useState(cat.colour)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!name.trim()) return
    setSaving(true)
    try {
      await rrUpdateCategory(cat.id, { name: name.trim(), colour })
      onSaved()
      onClose()
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900">Edit category</h3>
            <p className="text-xs text-gray-500 mt-1">Update the name or accent colour.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-1.5">Category name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandColor/30 focus:border-brandColor" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">Accent colour</label>
            <div className="flex gap-2 flex-wrap">
              {SWATCHES.map(c => (
                <button key={c} onClick={() => setColour(c)} className={`w-8 h-8 rounded-lg transition-all ${c === colour ? 'ring-2 ring-offset-2 ring-gray-800' : ''}`} style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/60">
          <button onClick={onClose} className="h-9 px-4 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={!name.trim() || saving} className="h-9 px-4 bg-brandColor text-white rounded-lg text-sm font-semibold hover:bg-brand-hover inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit2 className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [activeCatId, setActiveCatId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [editingCat, setEditingCat] = useState<Category | null>(null)
  const [loadingCats, setLoadingCats] = useState(true)
  const [loadingProds, setLoadingProds] = useState(false)

  const loadCategories = useCallback(async () => {
    setLoadingCats(true)
    try {
      const res = await rrGetCategories()
      const cats: Category[] = (res?.data ?? []) as Category[]
      setCategories(cats)
      if (cats.length && activeCatId === null) setActiveCatId(cats[0].id)
    } finally {
      setLoadingCats(false)
    }
  }, [activeCatId])

  useEffect(() => { loadCategories() }, [])

  useEffect(() => {
    if (!activeCatId) return
    setLoadingProds(true)
    rrGetCategoryProducts(activeCatId)
      .then(res => setProducts((res?.data ?? []) as Product[]))
      .finally(() => setLoadingProds(false))
  }, [activeCatId])

  async function toggleRR(catId: number, productId: number) {
    await rrToggleProductRR(catId, productId)
    // Optimistic update
    setProducts(prev => prev.map(p => p.product_id === productId ? { ...p, rr_enabled: !p.rr_enabled } : p))
  }

  const activeCat = categories.find(c => c.id === activeCatId)
  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()))
  const rrEnabledCount = filtered.filter(p => p.rr_enabled).length

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Categories & Products</h1>
            <p className="text-sm text-gray-500 mt-0.5">Organise synced Shopify products into the category structure that all round-robin routing is built on.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products or SKU…" className="w-full pl-9 pr-3 h-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandColor/30 focus:border-brandColor" />
          </div>
          <div className="flex-1" />
        </div>

        <div className="grid grid-cols-[300px_1fr] gap-4 items-start">
          {/* Left — categories */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Categories</span>
              <span className="text-xs font-bold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{categories.length}</span>
            </div>
            <div className="divide-y divide-gray-50 p-2">
              {loadingCats
                ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                : categories.map(cat => (
                  <div key={cat.id} className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition-colors ${cat.id === activeCatId ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                    <button className="flex-1 flex items-center gap-2.5 text-left min-w-0" onClick={() => setActiveCatId(cat.id)}>
                      <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cat.colour + '22' }}>
                        <Layers className="w-3.5 h-3.5" style={{ color: cat.colour }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{cat.name}</div>
                        <div className="text-xs text-gray-400">{cat.product_count} products · {cat.rr_count} RR</div>
                      </div>
                    </button>
                    <button onClick={e => { e.stopPropagation(); setEditingCat(cat) }} className="w-7 h-7 border border-gray-200 rounded-lg bg-white flex items-center justify-center text-gray-400 hover:text-brandColor hover:border-brandColor/30 flex-shrink-0">
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Right — products */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: activeCat ? activeCat.colour + '22' : '#eafaf1' }}>
                  <Layers className="w-5 h-5" style={{ color: activeCat?.colour ?? '#157a4e' }} />
                </div>
                <div>
                  <div className="text-base font-extrabold text-gray-900">{activeCat?.name ?? '—'}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{filtered.length} products · {rrEnabledCount} enabled for round robin</div>
                </div>
              </div>
              <button onClick={() => activeCat && setEditingCat(activeCat)} className="inline-flex items-center gap-1.5 h-8 px-3 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50">
                <Edit2 className="w-3.5 h-3.5" /> Rename
              </button>
            </div>

            <div className="overflow-x-auto">
              {loadingProds
                ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
                : <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="w-8 px-4 py-3" />
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Round Robin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map(p => (
                      <tr key={p.cp_id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3"><GripVertical className="w-4 h-4 text-gray-300 cursor-grab" /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-gray-400" />
                            </div>
                            <span className="font-semibold text-gray-900">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.shopify_product_id}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.sku || '—'}</td>
                        <td className="px-4 py-3 font-semibold text-gray-700">{p.price || '—'}</td>
                        <td className="px-4 py-3">
                          {p.product_status === 'active'
                            ? <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />Active</span>
                            : <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full capitalize">{p.product_status}</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          <RRToggle on={p.rr_enabled} onChange={() => activeCatId && toggleRR(activeCatId, p.product_id)} />
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && !loadingProds && (
                      <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">No products found</td></tr>

                    )}
                  </tbody>
                </table>
              }
            </div>

            <div className="flex items-start gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
              Toggle <b className="text-gray-600">Round Robin</b> to include or exclude a product from auto-assignment.
            </div>
          </div>
        </div>
      </div>

      {editingCat && <EditCategoryModal cat={editingCat} onClose={() => setEditingCat(null)} onSaved={loadCategories} />}
    </div>
  )
}
